'use client';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogHeader,
  AlertDialogFooter,
  Spinner,
} from '@ui/components';
import { useClickOutside } from '@ui/hooks';
import { useEffect } from 'react';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  topicId: string | null;
  onDeleteTopic?: (topicId: string) => Promise<void>;
  isDeleting?: boolean;
  isDeleteSuccess?: boolean;
}

export const TopicDeleteModal = ({
  open,
  setOpen,
  topicId,
  onDeleteTopic,
  isDeleting,
  isDeleteSuccess,
}: Props) => {
  const ref = useClickOutside(() => setOpen(false));

  const handleDelete = async () => {
    if (!onDeleteTopic || !topicId) {
      console.error('Delete operation failed: Missing required properties');
      return;
    }

    try {
      await onDeleteTopic(topicId);
    } catch (error) {
      console.error('Error deleting topic:', error);
    }
  };

  useEffect(() => {
    if (isDeleteSuccess) {
      setOpen(false);
    }
  }, [isDeleteSuccess]);

  if (!onDeleteTopic || !topicId) {
    return null;
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent ref={ref}>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action will permanently delete the topic. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700">
            {isDeleting && <Spinner className="mr-2 animate-spin" fill="#fff" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
